import type { SearchTabCounts } from '~/types/SearchTabCounts';

const TYPEFORM_EMBED_TYPES = [
  'widget',
  'popup',
  'slider',
  'sidetab',
  'popover'
] as const;

/** Embeds that attach to document body and must not reserve content-section space. */
const TYPEFORM_OVERLAY_EMBED_TYPES = [
  'popup',
  'slider',
  'sidetab',
  'popover'
] as const;

/** Native Typeform SDK auto-open triggers. */
export const TYPEFORM_SDK_OPEN_TYPES = [
  'load',
  'exit',
  'scroll',
  'time'
] as const;

/** App-specific open triggers handled outside the Typeform SDK. */
export const TYPEFORM_CUSTOM_OPEN_TYPES = ['search'] as const;

export const TYPEFORM_OPEN_TYPES = [
  ...TYPEFORM_SDK_OPEN_TYPES,
  ...TYPEFORM_CUSTOM_OPEN_TYPES
] as const;

/**
 * Delay before time-based auto-open when `open` is `search` (ms).
 * Weak results open immediately; otherwise the embed expands after this delay.
 * Overridable via configuration `openValue`.
 */
export const TYPEFORM_SEARCH_OPEN_DELAY_MS = 8000;

/**
 * Suggested CMS value for `weakResultsMaxTotal` when enabling early open on
 * weak search totals (`open: "search"`). Early open is off unless the field
 * is set in configuration.
 */
export const TYPEFORM_SEARCH_WEAK_RESULTS_MAX_TOTAL = 3;

export type TypeformEmbedType = (typeof TYPEFORM_EMBED_TYPES)[number];
export type TypeformSdkOpenType = (typeof TYPEFORM_SDK_OPEN_TYPES)[number];
export type TypeformCustomOpenType =
  (typeof TYPEFORM_CUSTOM_OPEN_TYPES)[number];
export type TypeformOpenType = (typeof TYPEFORM_OPEN_TYPES)[number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function normalizeTypeformEmbedType(value?: string): TypeformEmbedType {
  if (value && TYPEFORM_EMBED_TYPES.includes(value as TypeformEmbedType)) {
    return value as TypeformEmbedType;
  }

  return 'widget';
}

export function normalizeTypeformOpenType(
  value?: string
): TypeformOpenType | undefined {
  if (value && TYPEFORM_OPEN_TYPES.includes(value as TypeformOpenType)) {
    return value as TypeformOpenType;
  }

  return undefined;
}

/**
 * Maps our open config to a value the Typeform SDK understands.
 * Custom triggers (e.g. `search`) must not be forwarded.
 */
export function resolveTypeformSdkOpenOption(
  value?: string
): TypeformSdkOpenType | undefined {
  const openType = normalizeTypeformOpenType(value);
  if (!openType) {
    return undefined;
  }

  if ((TYPEFORM_CUSTOM_OPEN_TYPES as readonly string[]).includes(openType)) {
    return undefined;
  }

  return openType as TypeformSdkOpenType;
}

export function isTypeformSearchOpenType(value?: string): boolean {
  return normalizeTypeformOpenType(value) === 'search';
}

export function sumSearchTabCounts(counts: SearchTabCounts): number {
  return (
    counts.products + counts.software + counts.documents + counts.resources
  );
}

/**
 * Threshold for early open on weak results (`open: "search"`).
 * Only a non-negative finite config value enables the rule; otherwise
 * `undefined` (time-based open only).
 */
export function resolveTypeformWeakResultsMaxTotal(
  weakResultsMaxTotal?: number
): number | undefined {
  if (
    typeof weakResultsMaxTotal === 'number' &&
    Number.isFinite(weakResultsMaxTotal) &&
    weakResultsMaxTotal >= 0
  ) {
    return Math.floor(weakResultsMaxTotal);
  }

  return undefined;
}

export function isWeakSearchResultTotal(
  totalHits: number,
  weakResultsMaxTotal?: number
): boolean {
  const maxTotal = resolveTypeformWeakResultsMaxTotal(weakResultsMaxTotal);
  if (maxTotal === undefined) {
    return false;
  }

  return totalHits <= maxTotal;
}

/**
 * Delay for `open: "search"` when early open does not apply.
 * `openValue` is milliseconds when provided and non-negative; otherwise the
 * default delay applies.
 */
export function resolveTypeformSearchOpenDelayMs(openValue?: number): number {
  if (
    typeof openValue === 'number' &&
    Number.isFinite(openValue) &&
    openValue >= 0
  ) {
    return openValue;
  }

  return TYPEFORM_SEARCH_OPEN_DELAY_MS;
}

/**
 * True when configuration has a Typeform form id (minimum required to render).
 */
export function hasTypeformSurveyConfiguration(
  configuration: unknown
): configuration is Record<string, unknown> & { id: string } {
  return (
    isRecord(configuration) &&
    typeof configuration.id === 'string' &&
    configuration.id.length > 0
  );
}

/**
 * True when the Typeform config uses an overlay embed (sidetab, popup, …)
 * that must not create a content-section layout footprint.
 */
export function isTypeformOverlayConfiguration(
  configuration: unknown
): boolean {
  if (!hasTypeformSurveyConfiguration(configuration)) {
    return false;
  }

  const embedType = normalizeTypeformEmbedType(
    typeof configuration.type === 'string' ? configuration.type : undefined
  );

  return (TYPEFORM_OVERLAY_EMBED_TYPES as readonly string[]).includes(
    embedType
  );
}

/**
 * True when a typeformSurvey entry must not reserve content-section space:
 * missing/invalid config (nothing to render) or overlay embed (body-attached).
 */
export function isTypeformLayoutNeutralConfiguration(
  configuration: unknown
): boolean {
  if (!hasTypeformSurveyConfiguration(configuration)) {
    return true;
  }

  return isTypeformOverlayConfiguration(configuration);
}
